<div
  class="chat-widget-container"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onFilesDropped($event)"
>
  @if (isDraggingOver) {
    <div class="attachment-drop-overlay" data-testid="attachment-drop-overlay">
      <mat-icon>attach_file</mat-icon>
      <span>Drop files to attach</span>
    </div>
  }
  <ng-container *ngIf="selectedChat; else noChat">
    <!-- Chat Header -->
    <mat-toolbar class="chat-header">
      <div class="avatar-circle">
        <app-user-avatar [imageUrl]="selectedChat.imageUrl" [name]="selectedChat.name" />
      </div>
      <div class="chat-header-info">
        <span class="chat-name">{{ selectedChat.name }}</span>
        <span class="chat-status online">Online</span>
      </div>
      <span class="spacer"></span>
      <button
        *hasPermission="permissions.MESSAGING.EXPORT_TRANSCRIPT"
        mat-icon-button
        (click)="downloadTranscript()"
        matTooltip="Download Transcript"
        data-testid="download-transcript-button"
      >
        <mat-icon>download</mat-icon>
      </button>
      <button mat-icon-button [matMenuTriggerFor]="chatMenu" matTooltip="More options">
        <mat-icon>more_vert</mat-icon>
      </button>
      <button mat-icon-button (click)="close()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
      <mat-menu #chatMenu="matMenu">
        <button mat-menu-item>
          <mat-icon>person</mat-icon>
          <span>View Profile</span>
        </button>
        <button mat-menu-item>
          <mat-icon>notifications_off</mat-icon>
          <span>Mute Notifications</span>
        </button>
        <mat-divider></mat-divider>
        <button mat-menu-item>
          <mat-icon color="warn">delete</mat-icon>
          <span>Clear chat</span>
        </button>
      </mat-menu>
    </mat-toolbar>

    <mat-divider></mat-divider>

    <div class="messages-pane">
      <mat-card
        class="chat-match-nav"
        *ngIf="!matchNavDismissed && (selectedChat.matchingMessageIds?.length ?? 0) > 1"
      >
        <span class="match-nav-label">
          Searching for: <strong>{{ searchQuery }}</strong>
        </span>

        <span class="match-nav-count">
          {{ currentMatchIndex + 1 }} of {{ selectedChat.matchingMessageIds?.length }}
        </span>

        <button mat-icon-button aria-label="previous" (click)="jumpPrev(selectedChat)">
          <mat-icon>keyboard_arrow_up</mat-icon>
        </button>
        <button mat-icon-button aria-label="next" (click)="jumpNext(selectedChat)">
          <mat-icon>keyboard_arrow_down</mat-icon>
        </button>

        <button mat-icon-button aria-label="close" (click)="closeSearchPopup()">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card>

      <!-- Messages Area -->
      <div class="messages-area" #scrollContainer (scroll)="onMessagesScroll()">
        <div
          *ngFor="let message of selectedChat.messages"
          class="message-wrapper"
          [id]="'message-' + message.id"
          [class.sent]="message.fromMe && !message.is_system"
          [class.received]="!message.fromMe && !message.is_system"
          [class.system]="message.is_system"
          [class.pending]="message.status === 'pending'"
          [class.highlighted]="message.id === highlightedMessageId"
        >
          <mat-card class="message-card" appearance="outlined">
            @if (!message.is_system) {
              <div class="message-sender-row">
                <span class="message-sender-name">
                  {{ getSenderName(message) }}
                </span>
              </div>
            }
            <mat-card-content>
              @if (message.replyTo) {
                <div
                  class="reply-quote"
                  [class.deleted]="message.replyTo.is_deleted"
                  [class.clickable]="!message.replyTo.is_deleted"
                  [attr.role]="message.replyTo.is_deleted ? null : 'button'"
                  [attr.tabindex]="message.replyTo.is_deleted ? null : 0"
                  (click)="message.replyTo.is_deleted || jumpToMessage(message.replyTo.id)"
                  (keydown.enter)="message.replyTo.is_deleted || jumpToMessage(message.replyTo.id)"
                >
                  <span class="reply-quote-sender">{{ getReplyQuoteSenderName(message.replyTo) }}</span>
                  <span class="reply-quote-snippet">{{ getReplyQuoteSnippet(message.replyTo) }}</span>
                </div>
              }
              @if (message.is_deleted) {
                <p class="deleted-message">This message was deleted</p>
              } @else {
                @if (message.attachments?.length) {
                  <div class="attachment-grid" [class.single-image]="isSingleImageMessage(message)">
                    @for (attachment of message.attachments; track attachment.url) {
                      @if (isImageAttachment(attachment)) {
                        <img
                          class="attachment-thumb"
                          [src]="attachment.url"
                          [alt]="attachment.file_name"
                          (click)="onAttachmentClick(message, attachment)"
                          (load)="onAttachmentImageLoad()"
                        />
                      } @else {
                        <button
                          type="button"
                          class="attachment-chip"
                          (click)="onAttachmentClick(message, attachment)"
                        >
                          <mat-icon>{{ getAttachmentIcon(attachment) }}</mat-icon>
                          <span class="attachment-chip-name">{{ attachment.file_name }}</span>
                        </button>
                      }
                    }
                  </div>
                }
                @if (editingMessageId === message.id) {
                  <div class="edit-message-box">
                    <input
                      class="edit-message-input"
                      [(ngModel)]="editMessageText"
                      (keydown.enter)="saveEditedMessage(message)"
                      (keydown.escape)="cancelEditMessage()"
                    />
                    <div class="edit-actions">
                      <button mat-icon-button (click)="saveEditedMessage(message)">
                        <mat-icon>check</mat-icon>
                      </button>
                      <button mat-icon-button (click)="cancelEditMessage()">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </div>
                } @else if (message.is_system) {
                  <div class="system-message">
                    {{ message.content }}
                  </div>
                } @else {
                  <p
                    [innerHTML]="
                      message.content | highlight: searchQuery : message.id === activeMatchMessageId
                    "
                  ></p>
                  @if (message.is_edited) {
                    <span class="edited-label">(edited)</span>
                  }
                }
              }

              @if (!message.is_deleted && !message.is_system && !message.status) {
                <div class="message-actions">
                  <button mat-icon-button (click)="startReply(message)" matTooltip="Reply">
                    <mat-icon>reply</mat-icon>
                  </button>
                  @if (message.fromMe) {
                    <button mat-icon-button (click)="startEditMessage(message)">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button mat-icon-button (click)="deleteMessage(message)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  }
                </div>
              }

              <div class="message-meta">
                <span class="timestamp">{{ formatTime(message.created_at) }}</span>
                @if (message.status === 'pending') {
                  <span class="send-status pending">Sending…</span>
                } @else if (!message.is_system) {
                  <mat-icon
                    *ngIf="message.fromMe"
                    class="receipt-icon"
                    [class.read]="
                      message.MessageReadReceipts && message.MessageReadReceipts.length > 0
                    "
                    [matTooltip]="message.MessageReadReceipts?.length ? 'Read' : 'Delivered'"
                  >
                    done_all
                  </mat-icon>
                }
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        @if (showJumpToLatest) {
          <button
            type="button"
            class="jump-to-latest-btn"
            (click)="jumpToLatest()"
            aria-label="Jump to latest messages"
            data-testid="jump-to-latest-button"
          >
            <mat-icon aria-hidden="true">arrow_downward</mat-icon>
            Jump to latest
          </button>
        }

        <!-- Typing Indicator -->
        @if (getTypingLabel(selectedChat); as typingLabel) {
          <div class="typing-indicator" aria-live="polite" data-testid="typing-status-bar">
            <em>{{ typingLabel }}</em>
          </div>
        }
      </div>
    </div>

    <mat-divider></mat-divider>

    <!-- Input Area -->
    @if (selectedChat.is_disabled) {
      <div class="input-area input-area-disabled">
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="message-input">
          <input
            matInput
            [placeholder]="
              selectedChat.is_group
                ? 'All members of the group chat must share connections with each other to continue the conversation'
                : 'You must be connected with the other member of the chat to continue the conversation'
            "
            [disabled]="true"
          />
        </mat-form-field>
      </div>
    } @else {
      @if (pendingAttachments.length) {
        <div class="pending-attachments">
          @for (pending of pendingAttachments; track pending.file) {
            <div class="pending-attachment" [class.error]="pending.status === 'error'">
              @if (pending.status === 'uploading') {
                <mat-icon class="pending-spinner">hourglass_empty</mat-icon>
              } @else if (pending.status === 'error') {
                <mat-icon>error_outline</mat-icon>
              } @else {
                <mat-icon>{{ pending.attachment?.mime_type === 'application/pdf' ? 'picture_as_pdf' : (pending.attachment?.mime_type?.startsWith('image/') ? 'image' : 'insert_drive_file') }}</mat-icon>
              }
              <span class="pending-attachment-name">{{ pending.file.name }}</span>
              <button
                type="button"
                mat-icon-button
                (click)="removeAttachment(pending)"
                aria-label="Remove attachment"
                data-testid="remove-attachment-button"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
        </div>
      }
      @if (replyingToMessage) {
        <div class="reply-preview-bar">
          <mat-icon class="reply-preview-icon">reply</mat-icon>
          <div class="reply-preview-text">
            <span class="reply-preview-sender">{{ getSenderName(replyingToMessage) || 'You' }}</span>
            <span class="reply-preview-snippet">{{
              replyingToMessage.content?.trim() || '📎 Attachment'
            }}</span>
          </div>
          <button
            type="button"
            mat-icon-button
            (click)="cancelReply()"
            aria-label="Cancel reply"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <div class="input-area">
        <input
          #attachmentInput
          type="file"
          multiple
          [accept]="attachmentAccept"
          hidden
          (change)="onFilesSelected($event)"
        />
        <button
          *hasPermission="permissions.MESSAGING.ATTACH_FILES"
          mat-icon-button
          type="button"
          (click)="openAttachmentPicker()"
          [disabled]="!canAddMoreAttachments"
          matTooltip="Attach files"
          data-testid="attach-file-button"
        >
          <mat-icon>attach_file</mat-icon>
        </button>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="message-input">
          <input
            matInput
            placeholder="Type a message...."
            [(ngModel)]="newMessage"
            (ngModelChange)="sendTyping($event.length > 0)"
            (keydown.enter)="sendMessage()"
          />
        </mat-form-field>
        <button
          mat-fab
          color="primary"
          (click)="sendMessage()"
          [disabled]="(!newMessage.trim() && !pendingAttachments.length) || hasUploadingAttachments"
          matTooltip="Send"
        >
          <mat-icon>send</mat-icon>
        </button>
      </div>
    }
  </ng-container>

  <!-- No Chat Selected -->
  <ng-template #noChat>
    <div class="no-chat">
      <mat-icon>chat_bubble_outline</mat-icon>
      <h2>No conversation selected</h2>
      <p>Select a conversation to start chatting</p>
    </div>
  </ng-template>
</div>
